/**
 * documentsAdmin.service.ts — Admin > Forms & Templates > Documents: the
 * account's document library (the `documents` table: the forms sent for
 * signature) and its categories, through the API v5 calls the corporate
 * dashboard's Documents page uses, plus the two it lacked:
 *   - GET /documents (grouped by title / category / format, 100 groups a page:
 *     every page is read and the groups flattened), POST, PUT /{id},
 *     DELETE /{id} (soft: active 0), POST /{id}/restore,
 *     POST /documents/reorder {documents, categoryID} (the target category's
 *     whole order; files the listed documents under it);
 *   - POST /documents/upload — the file, saved in the account's legacy
 *     document store where signing and sending read it; answers the bare file
 *     name to save (corp wrote straight to S3, which the legacy paths can't read);
 *   - GET /documents/{id}/download?inline=true — a link (S3) or the file itself;
 *   - /document-categories: list (live ones), add, rename, delete (soft; no restore).
 *
 * `format` holds the Signature(s) choice: '' or a legacy code. Legacy's labels
 * are used (corp shows four of the five against the wrong codes). Keywords,
 * the e-mail / SMS template and packets are not edited here yet.
 * Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import { uploadFile } from '@/services/uploads.service';
import type { ApiResponse } from '@/types/api.types';

/** documents.format → the legacy admin's label. */
export const SIGNATURE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'patient', label: 'Patient Signature' },
  { value: 'patientpri', label: 'Patient Signature & Printed Name' },
  { value: 'patientwit', label: 'Patient & Witness Signatures' },
  { value: 'patientpwi', label: 'Patient Signature, Printed Name & Witness Signature' },
  { value: 'patientpgu', label: 'Patient Signature, Printed Name & Parent / Guardian Signature' },
];

/** The Signature(s) label; anything else (a file extension v5 used to store) is no signature. */
export function signatureLabel(format: string): string {
  return SIGNATURE_OPTIONS.find((o) => o.value === format)?.label ?? 'None';
}

/** A stored format that is a signature code ('' when it is not one). */
export function signatureCode(format: string): string {
  return SIGNATURE_OPTIONS.some((o) => o.value === format) ? format : '';
}

/** The file picker's types (legacy and corp). */
export const DOCUMENT_FILE_ACCEPT = '.doc,.docx,.rtf,.odt,.jpg,.jpeg,.pdf';
const DOCUMENT_EXTENSIONS = ['doc', 'docx', 'rtf', 'odt', 'jpg', 'jpeg', 'pdf'];

export function fileExtension(name: string): string {
  const base = name.split('/').pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
}

export function isAcceptedDocumentFile(name: string): boolean {
  return DOCUMENT_EXTENSIONS.includes(fileExtension(name));
}

/** The file's name without a folder (S3 keys carry one). */
export function fileBaseName(fileName: string): string {
  return fileName.split('/').pop() ?? fileName;
}

export interface AdminDocument {
  id: number;
  title: string;
  description: string;
  /** Bare name in the legacy store, or an S3 key (corp's uploads). '' = no file. */
  fileName: string;
  /** The Signature(s) code (see SIGNATURE_OPTIONS). */
  format: string;
  categoryId: number | null;
  /** False when its category is deleted; null when unknown. */
  categoryActive: boolean | null;
  order: number;
  active: boolean;
  /** Legacy's linked Email / SMS template (0 = none). */
  emailContentId: number;
  smsId: number;
}

export interface DocumentCategory {
  id: number;
  name: string;
}

const isActive = (v: unknown) => v !== false && v !== 0 && v !== '0';

export function toAdminDocument(row: Record<string, unknown>): AdminDocument {
  const category = (row.category ?? null) as Record<string, unknown> | null;
  return {
    id: Number(row.documentID ?? 0),
    title: String(row.title ?? ''),
    description: String(row.description ?? ''),
    fileName: String(row.fileName ?? ''),
    format: String(row.format ?? ''),
    categoryId: Number(row.categoryID ?? 0) || null,
    categoryActive: category && 'active' in category ? isActive(category.active) : null,
    order: Number(row.dOrder ?? 0) || 0,
    active: isActive(row.active),
    emailContentId: Number(row.emailContentID ?? 0) || 0,
    smsId: Number(row.smsID ?? 0) || 0,
  };
}

function rows(data: unknown): Record<string, unknown>[] {
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

/** Every document of the account, deleted ones too. */
export async function listAdminDocuments(): Promise<AdminDocument[]> {
  const out: AdminDocument[] = [];
  const seen = new Set<number>();
  for (let page = 1; ; page++) {
    const r = await apiClient.get<{ data?: unknown; meta?: { last_page?: number } }>('/documents', {
      params: { page, limit: 100, show_inactive: true },
    });
    for (const group of rows(r.data?.data)) {
      for (const doc of rows(group.documents)) {
        const d = toAdminDocument(doc);
        if (d.id && !seen.has(d.id)) {
          seen.add(d.id);
          out.push(d);
        }
      }
    }
    if (page >= (Number(r.data?.meta?.last_page ?? 1) || 1)) return out;
  }
}

export function listDocumentCategories(): Promise<DocumentCategory[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/document-categories').then((r) =>
    rows(r.data?.data).map((c) => ({ id: Number(c.documentCategoryID ?? c.id ?? 0), name: String(c.name ?? '') })),
  );
}

export function createDocumentCategory(name: string): Promise<DocumentCategory> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/document-categories', { name: name.trim() })
    .then((r) => ({ id: Number(r.data?.data?.documentCategoryID ?? 0), name: String(r.data?.data?.name ?? name.trim()) }));
}

export function renameDocumentCategory(id: number, name: string): Promise<void> {
  return apiClient.patch(`/document-categories/${id}`, { name: name.trim() }).then(() => undefined);
}

export function deleteDocumentCategory(id: number): Promise<void> {
  return apiClient.delete(`/document-categories/${id}`).then(() => undefined);
}

export interface DocumentDraft {
  title: string;
  description: string;
  fileName: string;
  format: string;
  emailContentId: number;
  smsId: number;
}

export function createAdminDocument(categoryId: number, d: DocumentDraft): Promise<AdminDocument> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/documents', {
      title: d.title.trim(),
      description: d.description.trim(),
      categoryID: categoryId,
      filename: d.fileName,
      format: d.format,
      emailContentID: d.emailContentId,
      smsID: d.smsId,
    })
    .then((r) => toAdminDocument(r.data?.data ?? {}));
}

/** The edited fields; the category is left to the board (drag). */
export function updateAdminDocument(id: number, d: DocumentDraft): Promise<void> {
  return apiClient
    .put(`/documents/${id}`, {
      title: d.title.trim(),
      description: d.description.trim(),
      fileName: d.fileName,
      format: d.format,
      emailContentID: d.emailContentId,
      smsID: d.smsId,
    })
    .then(() => undefined);
}

export function deleteAdminDocument(id: number): Promise<void> {
  return apiClient.delete(`/documents/${id}`).then(() => undefined);
}

export function restoreAdminDocument(id: number): Promise<void> {
  return apiClient.post(`/documents/${id}/restore`).then(() => undefined);
}

export function reorderAdminDocuments(categoryId: number, ids: number[]): Promise<void> {
  return apiClient.post('/documents/reorder', { documents: ids, categoryID: categoryId }).then(() => undefined);
}

/** Saves the file in the account's document store; answers the name to store on the document. */
export async function uploadDocumentFile(file: File, onProgress?: (percent: number) => void): Promise<string> {
  const r = await uploadFile('/documents/upload', file, undefined, { fileFieldName: 'file', onProgress });
  const name = r?.data?.data?.fileName as string | undefined;
  if (!name) throw new Error('The upload finished but no file name came back.');
  return name;
}

/**
 * A URL for the document's file: the API answers a link for a file on S3, or
 * the file itself for one in the legacy store (then a blob URL — revoke it).
 */
export async function getDocumentFileUrl(id: number): Promise<{ url: string; blob: boolean }> {
  const r = await apiClient.get<Blob>(`/documents/${id}/download`, { params: { inline: true }, responseType: 'blob' });
  const blob = r.data;
  if (blob.type.includes('application/json')) {
    const body = JSON.parse(await blob.text()) as { data?: { url?: string } };
    const url = body?.data?.url;
    if (!url) throw new Error('No file link came back.');
    return { url, blob: false };
  }
  return { url: URL.createObjectURL(blob), blob: true };
}
